import * as Yup from "yup";
import { validPhoneNumber } from "../helper";
import {
  alphanumericOnly,
  numberStringOnly,
} from "../FormComponents/Validations";
import { ChangeElectricityJobType } from "./helpers";

export const changeRequestPremiseValidationSchema = Yup.object().shape({
  requestingCompany: Yup.mixed().when("$noCompany", {
    is: (noCompany: boolean) => !noCompany,
    then: (schema) => schema.required("Please select"),
    otherwise: (schema) => schema.nullable(),
  }),
  nmi: Yup.string()
    .required("Oops, you must enter a NMI.")
    .matches(/^(?:\d{10}|[A-Za-z\d]{10})$/, "Oops, NMI must be 10 characters"),
});

export const changeRequestElectricityValidationSchema = Yup.object().shape({
  changeTypes: Yup.array().min(1, "At least one must be selected"),
  licensedElectrician: Yup.string().when("$noCompany", {
    is: (noCompany: boolean) => !noCompany,
    then: (schema) => schema.required("Please select"),
    otherwise: (schema) => schema.nullable(),
  }),
  electricianFields: Yup.object().shape({
    firstName: Yup.string().when("$noCompany", {
      is: true,
      then: (schema) =>
        schema
          .matches(
            /^[A-Za-z\s\-']+$/,
            "Only letters, hyphens, spaces and apostrophes allowed"
          )
          .required("Please enter the first name"),
      otherwise: (schema) => schema.nullable(),
    }),
    lastName: Yup.string().when("$noCompany", {
      is: true,
      then: (schema) =>
        schema
          .matches(
            /^[A-Za-z\s\-']+$/,
            "Only letters, hyphens, spaces and apostrophes allowed"
          )
          .required("Please enter the last name"),
      otherwise: (schema) => schema.nullable(),
    }),
    phoneNumber: Yup.string().when("$noCompany", {
      is: true,
      then: (schema) =>
        schema
          .test({
            message: "Please enter a valid 10 digit phone number",
            test: (value) => validPhoneNumber(value as string),
          })
          .matches(
            numberStringOnly,
            "Please enter a valid 10 digit phone number"
          )
          .required("Please enter a valid 10 digit phone number"),
      otherwise: (schema) => schema.nullable(),
    }),
    emailAddress: Yup.string().when("$noCompany", {
      is: true,
      then: (schema) =>
        schema
          .email("Please enter a valid email address")
          .required("Please enter an email address"),
      otherwise: (schema) => schema.nullable(),
    }),
    licenceNumber: Yup.string().when("$noCompany", {
      is: true,
      then: (schema) =>
        schema
          .matches(alphanumericOnly, "Numbers and letters only")
          .required("Numbers and letters only"),
      otherwise: (schema) => schema.nullable(),
    }),
  }),
  supplyDetails: Yup.object().shape({
    maxLoad: Yup.string() // Changed from number to string
      .required("Please enter a number")
      .test(
        "is-number",
        "Please enter a number",
        (value) => !isNaN(Number(value))
      )
      .test(
        "min-value",
        "Enter a value greater than 1",
        (value) => Number(value) > 1
      ),
  }),
  meteringRequirements: Yup.object().shape({
    offPeakRequirements: Yup.array().when("$showOffPeakRequirements", {
      is: true,
      then: (schema) => schema.min(1, "Please select"),
      otherwise: (schema) => schema.nullable(),
    }),
    otherOffPeakRequirements: Yup.string().when("offPeakRequirements", {
      is: (offPeakRequirements: string[]) =>
        offPeakRequirements.includes("other"),
      then: (schema) => schema.required("Please provide details"),
      otherwise: (schema) => schema.nullable(),
    }),
  }),
  meterAccess: Yup.object().shape({
    accessInstructions: Yup.string().when("meterBehindGate", {
      is: "yes",
      then: (schema) => schema.required("Please provide details"),
      otherwise: (schema) => schema.nullable(),
    }),
  }),
  solarInstallationDetails: Yup.object().shape({
    installationDate: Yup.mixed().when("$showInstallationDate", {
      is: true,
      then: (schema) =>
        schema
          .nullable()
          .required("Date format should be DD/MM/YYYY")
          .test(
            "is-valid-date",
            "Date format should be DD/MM/YYYY",
            (value) => {
              if (value === null) return false; // Fail if null (required)
              return value instanceof Date && !isNaN(value.getTime());
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  }),
  supportingDocuments: Yup.object().shape({
    rexId: Yup.string().when(
      ["$showRexId", "$onlySolar", "$coordinationOfElectricalWorks"],
      {
        is: (
          showRexId: boolean,
          onlySolar: boolean,
          coordinationOfElectricalWorks: "yes" | "no"
        ) =>
          // For solar only, it is optional if coordinationOfElectricalWorks is no
          // For others, when showRexId is true, it is mandatory
          showRexId &&
          (onlySolar ? coordinationOfElectricalWorks === "yes" : true),
        then: (schema) =>
          schema
            .matches(numberStringOnly, "Numbers only")
            .required("Numbers only"),
        otherwise: (schema) => schema.nullable(),
      }
    ),
    coesReferenceNumber: Yup.string().when(
      ["$showCoesReferenceNumber", "$changeTypes"],
      {
        is: (showCoesReferenceNumber: boolean, changeTypes: string[] = []) =>
          showCoesReferenceNumber &&
          changeTypes.includes(ChangeElectricityJobType.INSTALL_SOLAR),
        then: (schema) =>
          schema
            .matches(alphanumericOnly, "Numbers and letters only")
            .required("Please enter a reference number"),
        otherwise: (schema) => schema.nullable(),
      }
    ),
  }),
  coesFiles: Yup.array().when(["$showCoesFiles", "changeTypes"], {
    is: (showCoesFiles: boolean, changeTypes: string[] = []) =>
      showCoesFiles &&
      changeTypes.includes(ChangeElectricityJobType.INSTALL_SOLAR),
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
    otherwise: (schema) => schema.nullable(),
  }),

  ewrFiles: Yup.array().when("$showEwrFiles", {
    is: true,
    then: (schema) =>
      schema
        .min(1, "Please upload a document")
        .required("Please upload a document"),
    otherwise: (schema) => schema.nullable(),
  }),
});
